import React from 'react'
import type { PeopleFilters } from '../../types/people'
import { normalizePeopleFilters, DEFAULT_PEOPLE_FILTERS } from '../../types/people'

type Props = {
  open: boolean
  onClose: () => void
  filters: Partial<PeopleFilters>
  onApply: (next: PeopleFilters) => void
  onClear?: () => void
}

export default function PeopleFilterDrawer({ open, onClose, filters, onApply, onClear }: Props) {
  const [local, setLocal] = React.useState<PeopleFilters>(normalizePeopleFilters(filters))
  React.useEffect(() => setLocal(normalizePeopleFilters(filters)), [filters])

  function apply() { onApply(normalizePeopleFilters(local)) }
  function clear() {
    setLocal({ ...DEFAULT_PEOPLE_FILTERS })
    onClear?.()
  }

  function toggleProsToGo(enabled: boolean) {
    setLocal((p) => ({
      ...p,
      prosToGo: enabled,
      prosOnly: enabled ? true : p.prosOnly,
      nearMe: enabled ? p.nearMe : false,
      zipcode: enabled ? p.zipcode : '',
    }))
  }

  return (
    <div aria-hidden={!open}>
      <div
        className={`fixed inset-0 bg-black/50 transition-opacity ${open ? 'opacity-100' : 'pointer-events-none opacity-0'} z-40`}
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        className={`fixed right-0 top-0 h-full w-[360px] md:w-[420px] bg-neutral-900 border-l border-white/10 shadow-2xl z-50 transform transition-transform ${open ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <div className="sticky top-0 z-10 bg-neutral-900 border-b border-white/10 px-3 py-2">
          <div className="flex items-center gap-2">
            <div className="text-base font-extrabold">Filters</div>
            <button onClick={onClose} className="ml-auto text-white/80 hover:text-white">×</button>
          </div>
        </div>

        <div className="p-4 space-y-6">
          {/* Sort */}
          <div>
            <div className="text-sm text-white/70 mb-2">Sort</div>
            <div className="flex flex-wrap gap-2">
              {([
                { key: 'trending', label: 'Trending' },
                { key: 'newest', label: 'Newest' },
              ] as const).map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setLocal((p) => ({ ...p, sort: opt.key }))}
                  className={`px-3 py-1.5 rounded-full border text-xs ${
                    local.sort === opt.key
                      ? 'bg-fuchsia-900/30 border-fuchsia-700/50 text-fuchsia-200'
                      : 'bg-white/5 border-white/10 text-white/85 hover:bg-white/10'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-sm text-white/70 mb-2">Pros</div>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={local.prosOnly}
                  onChange={(e) => setLocal((p) => ({ ...p, prosOnly: e.target.checked || p.prosToGo }))}
                />
                <span>Pros only</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={local.prosToGo}
                  onChange={(e) => toggleProsToGo(e.target.checked)}
                />
                <span>Pros to Go (Julian beta)</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={local.nearMe}
                  onChange={(e) => setLocal((p) => ({ ...p, nearMe: e.target.checked, prosOnly: p.prosOnly || p.prosToGo || e.target.checked }))}
                  disabled={!local.prosToGo}
                />
                <span className={!local.prosToGo ? 'text-white/50' : ''}>Near Me</span>
              </label>
              {local.prosToGo ? (
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <label className="block text-xs text-white/70">
                    ZIP code
                    <input
                      value={local.zipcode}
                      onChange={(e) => setLocal((p) => ({ ...p, zipcode: e.target.value }))}
                      className="mt-1 w-full rounded-lg border border-white/15 bg-black px-3 py-2 text-sm"
                      placeholder="92036"
                    />
                  </label>
                  <label className="block text-xs text-white/70">
                    Radius
                    <select
                      value={local.radiusMiles ? String(local.radiusMiles) : ''}
                      onChange={(e) => {
                        const raw = (e.target.value || '').trim()
                        setLocal((p) => ({ ...p, radiusMiles: raw ? Number(raw) : null }))
                      }}
                      className="mt-1 w-full rounded-lg border border-white/15 bg-black px-3 py-2 text-sm"
                    >
                      <option value="">Any distance</option>
                      <option value="10">10 miles</option>
                      <option value="25">25 miles</option>
                      <option value="50">50 miles</option>
                      <option value="100">100 miles</option>
                    </select>
                  </label>
                </div>
              ) : null}
            </div>
          </div>

          {/* Audience */}
          <div>
            <div className="text-sm text-white/70 mb-2">Audience</div>
            <div className="flex flex-wrap gap-2">
              {([
                { key: 'all', label: 'All' },
                { key: 'friends', label: 'Friends' },
                { key: 'following', label: 'Following' },
              ] as const).map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setLocal((p) => ({ ...p, audience: opt.key }))}
                  className={`px-3 py-1.5 rounded-full border text-xs ${
                    local.audience === opt.key
                      ? 'bg-fuchsia-900/30 border-fuchsia-700/50 text-fuchsia-200'
                      : 'bg-white/5 border-white/10 text-white/85 hover:bg-white/10'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Gender */}
          <div>
            <div className="text-sm text-white/70 mb-2">Gender</div>
            <div className="flex flex-wrap gap-2">
              {([
                { key: 'any', label: 'Any' },
                { key: 'women', label: 'Women' },
                { key: 'men', label: 'Men' },
              ] as const).map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setLocal((p) => ({ ...p, gender: opt.key }))}
                  className={`px-3 py-1.5 rounded-full border text-xs ${
                    local.gender === opt.key
                      ? 'bg-fuchsia-900/30 border-fuchsia-700/50 text-fuchsia-200'
                      : 'bg-white/5 border-white/10 text-white/85 hover:bg-white/10'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Age Group */}
          <div>
            <div className="text-sm text-white/70 mb-2">Age Group</div>
            <div className="flex flex-wrap gap-2">
              {([
                { key: '18_24', label: '18-24' },
                { key: '25_34', label: '25-34' },
                { key: '35_44', label: '35-44' },
                { key: '45p', label: '45+' },
              ] as const).map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setLocal((p) => ({ ...p, ageGroup: p.ageGroup === (opt.key as any) ? null : (opt.key as any) }))}
                  className={`px-3 py-1.5 rounded-full border text-xs ${
                    local.ageGroup === (opt.key as any)
                      ? 'bg-fuchsia-900/30 border-fuchsia-700/50 text-fuchsia-200'
                      : 'bg-white/5 border-white/10 text-white/85 hover:bg-white/10'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="pt-2 flex items-center justify-between">
            <button onClick={clear} className="text-sm text-white/70 hover:text-white">Clear</button>
            <button onClick={apply} className="px-3 py-2 rounded-lg bg-brand/80 hover:bg-brand text-sm font-semibold">Apply</button>
          </div>
        </div>
      </div>
    </div>
  )
}
